import type {
  VendorChunkedUploadCompleteRequest,
  VendorChunkedUploadCompleteResponse,
  VendorChunkedUploadInitRequest,
  VendorChunkedUploadInitResponse,
} from "../../types/vendor-chunked-upload";

const normalizeBaseUrl = (baseUrl: string): string => baseUrl.replace(/\/$/, "");

const vendorAuthHeaders = (
  bearer: string,
  publishableKey: string
): Record<string, string> => ({
  authorization: `Bearer ${bearer}`,
  "x-publishable-api-key": publishableKey,
});

const jsonHeaders = (
  bearer: string,
  publishableKey: string
): Record<string, string> => ({
  ...vendorAuthHeaders(bearer, publishableKey),
  "Content-Type": "application/json",
});

const parseErrorMessage = async (response: Response): Promise<string> => {
  const text = await response.text();
  if (!text.trim()) {
    return response.statusText || "Server error";
  }
  try {
    const data = JSON.parse(text) as { message?: unknown };
    if (data && typeof data === "object" && "message" in data) {
      return String(data.message);
    }
  } catch {
    return text.trim();
  }
  return text.trim();
};

const throwHttpError = async (response: Response): Promise<never> => {
  const message = await parseErrorMessage(response);
  const error = new Error(message);
  (error as Error & { status: number }).status = response.status;
  throw error;
};

export const initVendorChunkedUpload = async (
  baseUrl: string,
  publishableKey: string,
  bearer: string,
  body: VendorChunkedUploadInitRequest
): Promise<VendorChunkedUploadInitResponse> => {
  const url = `${normalizeBaseUrl(baseUrl)}/vendor/chunked-uploads`;
  const response = await fetch(url, {
    method: "POST",
    headers: jsonHeaders(bearer, publishableKey),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return (await response.json()) as VendorChunkedUploadInitResponse;
};

export const putVendorChunkedUploadChunk = async (
  baseUrl: string,
  publishableKey: string,
  bearer: string,
  uploadId: string,
  chunkIndex: number,
  blob: Blob
): Promise<void> => {
  const root = normalizeBaseUrl(baseUrl);
  const url = `${root}/vendor/chunked-uploads/${encodeURIComponent(uploadId)}/chunks/${chunkIndex}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      ...vendorAuthHeaders(bearer, publishableKey),
      "Content-Type": "application/octet-stream",
      "Content-Length": String(blob.size),
    },
    body: blob,
  });

  if (!response.ok) {
    await throwHttpError(response);
  }
};

export const completeVendorChunkedUpload = async (
  baseUrl: string,
  publishableKey: string,
  bearer: string,
  uploadId: string,
  body: VendorChunkedUploadCompleteRequest
): Promise<VendorChunkedUploadCompleteResponse> => {
  const root = normalizeBaseUrl(baseUrl);
  const url = `${root}/vendor/chunked-uploads/${encodeURIComponent(uploadId)}/complete`;

  const response = await fetch(url, {
    method: "POST",
    headers: jsonHeaders(bearer, publishableKey),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return (await response.json()) as VendorChunkedUploadCompleteResponse;
};

/** Best-effort cleanup; errors are swallowed. */
export const abortVendorChunkedUpload = async (
  baseUrl: string,
  publishableKey: string,
  bearer: string,
  uploadId: string
): Promise<void> => {
  const root = normalizeBaseUrl(baseUrl);
  const url = `${root}/vendor/chunked-uploads/${encodeURIComponent(uploadId)}`;

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: vendorAuthHeaders(bearer, publishableKey),
    });
    if (!response.ok && response.status !== 404) {
      await response.text();
    }
  } catch {
    // ignore
  }
};
